import { Box, IconButton, Paper, Skeleton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from "@mui/material";
import { getRequestConfig } from "../auth/auth";
import axios from "axios";
import { Fragment, useCallback, useEffect, useState, useRef } from "react";
import { LoadingButton } from "@mui/lab";
import UploadIcon from '@mui/icons-material/UploadFile'


// Sanitize a filename so it contains only URL/routing-safe characters.
// Spaces and characters like () [] {} that break backend CORS preflight routing
// are replaced with underscores. The file extension is preserved.
const sanitizeFileName = (name) => {
  const lastDot = name.lastIndexOf('.')
  const ext = lastDot !== -1 ? name.slice(lastDot) : ''
  const base = (lastDot !== -1 ? name.slice(0, lastDot) : name)
    .replace(/\s+/g, '_')
    .replace(/[()\[\]{}&$@=;:+,?\\^`~<>|#%"'!*]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
  return (base || 'file') + ext
}

// Strict encoding helper for filenames sent as query parameter values.
const encodeFileNameQueryParam = (fileName) =>
  encodeURIComponent(fileName)
    .replace(/\(/g, '%28')
    .replace(/\)/g, '%29')
    .replace(/'/g, '%27')
    .replace(/!/g, '%21')
    .replace(/\*/g, '%2A')

// Path-segment encoding should match AWS behavior for authorization file routes,
// where parentheses remain unescaped (e.g. "(2)") while spaces are encoded.
const encodeFileNamePathSegment = (fileName) => encodeURIComponent(fileName)

export default function FileListControl(props={
  rowData: [], 
  showLastModified: false, 
  showActions: false, 
  enableUpload: false,
  enableDownload: false, 
  listFilesUrl: undefined,
  getFileUrlServiceAddr: undefined, 
  getFileUrlServiceQueryParams: undefined,
  getPutFileUrlServiceAddr: undefined,
  getPutFileUrlServiceQueryParams: undefined,
  removeFileUrl: undefined,
  removeFileUrlQueryParams: undefined,
  onFileUploaded: undefined,
  onFileRemoved: undefined,
  altFileNameColumnLabel: undefined
  }) {
  
  const [rowData, setRowData] = useState(props.rowData || [])
  const [targetFile, setTargetFile] = useState()
  const [targetFileName, setTargetFileName] = useState('')
  const [isUploading, setIsUploading] = useState(false)
  const fileInputRef = useRef(null);
  const [clickTarget, setClickTarget] = useState()
  const [isGetting, setIsGetting] = useState(false)
  const [isRemoving, setIsRemoving] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const [loadingFiles, setLoadingFiles] = useState(false)

  const selectFile = ((event) => {
    const _targetFile = event.target.files[0]
    if(_targetFile) {
      setTargetFile(_targetFile)
      setTargetFileName(sanitizeFileName(_targetFile.name))
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  })

  const clearFile = () => {
    setTargetFile()
    setTargetFileName('')
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  }

  const listFiles = useCallback(async () => {
    setErrorMessage('')
    if (props.listFilesUrl) {
      setLoadingFiles(true)
      const requestConfig = await getRequestConfig()
      const basePath = props.listFilesUrl
      try {
        const response = await axios.get(`${basePath}`, requestConfig)
        const sorted = (response.data ?? []).sort((a, b) => a.fileName.localeCompare(b.fileName))
        setRowData(sorted)
      } catch (error) {
        setErrorMessage(error.response?.data ?? error.message ?? error)
      } finally {
        setLoadingFiles(false)
      }
    } else {
      setRowData(props.rowData || [])
    }
  }, [props.listFilesUrl, props.rowData])

  const onRequestUploadFile = async () => {
    setErrorMessage('')
    setIsUploading(true)

    const requestConfig = await getRequestConfig()
    // targetFileName is already sanitized (set during file selection)
    const fileName = targetFileName

    const serviceUrl = [      
      props.getPutFileUrlServiceAddr,
      `?fileName=${encodeFileNameQueryParam(fileName)}`
    ]
    if (props.getPutFileUrlServiceQueryParams) {
      serviceUrl.push(`&${props.getPutFileUrlServiceQueryParams}`)
    }
    try {
      // PUT the file binary directly through the backend to avoid browser→blob direct calls
      // that are blocked by storage network rules outside the VDI.
      await axios.put(serviceUrl.join(''), targetFile, {
        ...requestConfig,
        headers: {
          ...requestConfig.headers,
          // Some upstream gateway rules reject PUT for certain MIME types (for example pdf/png).
          // Send binary as octet-stream and let the backend store/process by filename.
          'Content-Type': 'application/octet-stream',
        },
        transformRequest: [(data) => data],
      })
      clearFile()
      await listFiles()
      if (props.onFileUploaded) {
        if (props.onFileUploaded.constructor.name === 'AsyncFunction') 
          await props.onFileUploaded()
        else
          props.onFileUploaded()
      }
    } catch (error) {
      setErrorMessage(error.response?.data ?? error.message ?? error)
    } finally {
      setIsUploading(false)
      clearFile()
    }
  }
  const downloadFile = async (fileName) => {
    setErrorMessage('')
    setClickTarget(fileName)
    setIsGetting(true)

    const requestConfig = await getRequestConfig()

    const serviceUrl = [      
      props.getFileUrlServiceAddr,
      encodeFileNamePathSegment(fileName)
    ]
    if (props.getFileUrlServiceQueryParams) {
      serviceUrl.push(`?${props.getFileUrlServiceQueryParams}`)
    }

    try {
      const response = await axios.get(serviceUrl.join(''), {
        ...requestConfig,
        responseType: 'blob',
      })
      if (response.data) {
        const blobUrl = URL.createObjectURL(new Blob([response.data], { type: response.headers['content-type'] }))
        const tempLink = document.createElement('a')
        tempLink.href = blobUrl
        tempLink.download = fileName
        tempLink.click()
        URL.revokeObjectURL(blobUrl)
      }
    } catch (error) {
      setErrorMessage(error.response?.data ?? error.message ?? error)
    } finally {
      setIsGetting(false)
      setClickTarget()
    }
  }

  const removeFile = async (fileName) => {
    setErrorMessage('')
    setClickTarget(fileName)
    setIsRemoving(true)
    const requestConfig = await getRequestConfig()

    const serviceUrl = [      
      props.removeFileUrl,
      `?fileName=${encodeFileNameQueryParam(fileName)}`
    ]
    if (props.removeFileUrlQueryParams) {
      serviceUrl.push(`&${props.removeFileUrlQueryParams}`)
    }

    try {
      await axios.delete(serviceUrl.join(''), requestConfig)
      await listFiles()
      if (props.onFileRemoved) props.onFileRemoved()
    } catch (error) {
      setErrorMessage(error.response?.data ?? error.message ?? error)
    }

    setTimeout(() => {
      setClickTarget()
      setIsRemoving(false)
    }, 3000)
  }

  useEffect(() => {
    listFiles()
  }, [listFiles])

  return (
    <Stack rowGap={1}>
      {props.enableUpload && 
      <Stack direction='row' spacing={2}>        
        <TextField 
                    // className='tis-form-field'
                      //variant="standard"          
          type="text"
          size='small'
          placeholder="Select a File"
          disabled
          value={targetFileName}
          InputProps={{
            endAdornment: (
              <IconButton component="label" size="small">
                <UploadIcon />
                <input 
                  ref={fileInputRef}
                  styles={{display:"none"}} 
                  hidden 
                  type="file" 
                  onChange={selectFile}
                  size='small'
                  />                
              </IconButton>
            ),
          }}
        />
        
        {targetFile && <LoadingButton variant='outlined' loading={isUploading} onClick={onRequestUploadFile}>Upload File</LoadingButton>}
      </Stack>
      }
      {(props.showFileList === undefined || props.showFileList === true) && <>
        { !!(errorMessage.length) && 
          <Box item sm={12} md={12} paddingBottom={3}>
            <Typography className='tis-alert-error' sx={{ color: '#FFF', padding: '5px'}} >{errorMessage}</Typography>
          </Box>
        }
        { ( !rowData || !rowData.length) && !loadingFiles &&
          <Typography>No files available</Typography>
        }
        {
          loadingFiles && <Skeleton />
        }
        { !!(rowData.length) && !props.noTable && <TableContainer component={Paper} variant="outlined">        
          <Table sx={{width: '100%' }} size='small'>
            <TableHead>
              <TableRow>
                <TableCell>File&nbsp;Name</TableCell>
                { props.altFileNameColumnLabel && 
                <TableCell>{ props.altFileNameColumnLabel}</TableCell>}
                { props.showLastModified && 
                <TableCell>Last&nbsp;Modified</TableCell>}
                { props.showActions && 
                <TableCell>Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {rowData.map(( row ) => (
                  <TableRow
                    key={row.fileName}
                  >
                    <TableCell align="left">
                      { props.enableDownload && props.getFileUrlServiceAddr &&
                        <LoadingButton 
                          loading={isGetting && clickTarget === row.fileName} 
                          variant="text" 
                          onClick={() => downloadFile(row.fileName)}
                          sx={{ textTransform: 'none', textAlign: 'left', padding: 0 }}>
                          {row.fileName}
                        </LoadingButton>
                      }
                      {(!props.enableDownload || !props.getFileUrlServiceAddr) && row.fileName}
                      </TableCell>
                      { props.altFileNameColumnLabel &&
                      <TableCell>
                        { row.altFileName }
                      </TableCell>
                      }
                    { props.showLastModified && 
                      <TableCell>{new Date(row.lastModified).toLocaleString().split(',').join('')}</TableCell>}
                    { props.showActions &&
                      <TableCell>
                        <LoadingButton 
                          loading={isRemoving && clickTarget === row.fileName} 
                          variant='text' 
                          onClick={() => removeFile(row.fileName )}
                          sx={{ textTransform: 'none' }}
                          >Remove</LoadingButton>
                      </TableCell>}
                  </TableRow>
                ))
              }
            </TableBody>
          </Table>
        </TableContainer>}

        {props.noTable && <div>
          {rowData.map((row, idx) => <Fragment key={idx}>
            {props.enableDownload && props.getFileUrlServiceAddr &&
              <LoadingButton
                loading={isGetting && clickTarget === row.fileName} 
                variant="text" 
                onClick={() => downloadFile(row.fileName)}
                sx={{ textTransform: 'none', textAlign: 'left', padding: 0 }}>
                {row.fileName}
              </LoadingButton>
            }
          </Fragment>)}
        </div>}
      </>}
    </Stack>
  )
}